import { useEffect, useState } from "react";
import swal from "../../utils/swal";
import { useGetFeePolicy, useUpdateFeePolicy } from "../../query/settingsQuery";

const EMPTY = {
  dueDayOfMonth: 10,
  graceDays: 0,
  lateFeeMode: "NONE",
  lateFeeAmount: 0,
  lateFeeCap: 0,
  reminderDaysBeforeDue: 3,
  accountTitle: "",
  method: "",
  accountNumber: "",
  note: "",
};

export default function FeePolicyPanel() {
  const { data: policy, isLoading } = useGetFeePolicy();
  const { mutate: savePolicy, isPending } = useUpdateFeePolicy();
  const [form, setForm] = useState(EMPTY);

  useEffect(() => {
    if (!policy) return;
    const instructions = policy.paymentInstructions || {};
    setForm({
      dueDayOfMonth: policy.dueDayOfMonth ?? 10,
      graceDays: policy.graceDays ?? 0,
      lateFeeMode: policy.lateFeeMode || "NONE",
      lateFeeAmount: policy.lateFeeAmount ?? 0,
      lateFeeCap: policy.lateFeeCap ?? 0,
      reminderDaysBeforeDue: policy.reminderDaysBeforeDue ?? 3,
      accountTitle: instructions.accountTitle || "",
      method: instructions.method || "",
      accountNumber: instructions.accountNumber || "",
      note: instructions.note || "",
    });
  }, [policy]);

  const set = (key) => (event) => {
    setForm((current) => ({ ...current, [key]: event.target.value }));
  };

  const save = () => {
    savePolicy(
      {
        dueDayOfMonth: Number(form.dueDayOfMonth),
        graceDays: Number(form.graceDays),
        lateFeeMode: form.lateFeeMode,
        lateFeeAmount: Number(form.lateFeeAmount) || 0,
        lateFeeCap: Number(form.lateFeeCap) || 0,
        reminderDaysBeforeDue: Number(form.reminderDaysBeforeDue) || 0,
        paymentInstructions: {
          accountTitle: form.accountTitle,
          method: form.method,
          accountNumber: form.accountNumber,
          note: form.note,
        },
      },
      {
        onSuccess: () => swal("Saved", "Fee policy updated", "success"),
        onError: (error) => swal("Error", error?.message || "Could not save fee policy", "error"),
      }
    );
  };

  if (isLoading && !policy) {
    return (
      <div className="settings-panel-body">
        <p className="settings-muted">Loading fee policy…</p>
      </div>
    );
  }

  return (
    <div className="settings-panel-body">
      <div className="settings-panel-intro">
        <div>
          <h2>Fee policy</h2>
          <p>
            These rules apply when vouchers are sent. Late fees are calculated on unpaid vouchers and frozen when a voucher is marked paid. Parents pay outside the app using the instructions below.
          </p>
        </div>
      </div>

      <div className="settings-rule-grid">
        <label>
          Due day of month
          <input type="number" min="1" max="28" value={form.dueDayOfMonth} onChange={set("dueDayOfMonth")} />
        </label>
        <label>
          Grace days
          <input type="number" min="0" max="60" value={form.graceDays} onChange={set("graceDays")} />
        </label>
        <label>
          Late fee
          <select value={form.lateFeeMode} onChange={set("lateFeeMode")}>
            <option value="NONE">None</option>
            <option value="FLAT">One flat charge after the grace period</option>
            <option value="PER_DAY">Charge for each day after the grace period</option>
          </select>
        </label>
        <label>
          Late fee amount (PKR)
          <input type="number" min="0" value={form.lateFeeAmount} onChange={set("lateFeeAmount")} />
        </label>
        <label>
          Late fee cap (0 = no cap)
          <input type="number" min="0" value={form.lateFeeCap} onChange={set("lateFeeCap")} />
        </label>
        <label>
          Reminder days before due
          <input type="number" min="0" max="28" value={form.reminderDaysBeforeDue} onChange={set("reminderDaysBeforeDue")} />
        </label>
        <label>
          Account title
          <input value={form.accountTitle} onChange={set("accountTitle")} placeholder="Birchwood Academy" />
        </label>
        <label>
          Bank or method
          <input value={form.method} onChange={set("method")} placeholder="Bank transfer" />
        </label>
        <label>
          Account or wallet number
          <input value={form.accountNumber} onChange={set("accountNumber")} placeholder="0000 0000 0000" />
        </label>
      </div>
      <label className="fee-policy-note">
        Note for parents
        <textarea rows={3} value={form.note} onChange={set("note")} placeholder="Pay the exact amount and upload the receipt in the app." />
      </label>
      <button type="button" className="settings-save" onClick={save} disabled={isPending}>
        {isPending ? "Saving…" : "Save fee policy"}
      </button>
    </div>
  );
}
